import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import CompanyLogoMarquee from "@/components/CompanyLogoMarquee";
import { Button } from "@/components/ui/button";
import { ArrowRight, Download } from "lucide-react";
import { useEffect, type ReactNode } from "react";
import SEO from "@/components/SEO";
import { trackEvent } from "@/utils/posthog";
import SpeakingInquiry from "@/components/SpeakingInquiry";
import "./Speak.css";

const CONTACT = "mailto:amelia@technical-leaders.com?subject=Speaking%20Inquiry";
const REVISION = "2026-10-23";

// User-approved excerpts from the AI Elevate Cohort 1 feedback summary, page 2.
// Brackets preserve the report's original clarity/anonymity edits.
const participantQuotes = [
  "He made everyone feel comfortable with getting out of [our] shells to share our ideas.",
  "Each session gave me something I could take back to [my site] and use that week.",
  "I now look at repetitive work and ask whether AI can help prepare it, track it, or follow up on it, while I keep the decisions.",
];

const topics = [
  {
    title: "AI Adoption & the 4 Gears of AI Usage",
    audience: "For leaders deciding where AI fits",
    takeaway: "Give your audience a way to choose between chat, assistants, agents, and automated loops, then apply the right approach to work they already do.",
  },
  {
    title: "Building Custom AI Agents",
    audience: "For teams ready to build",
    takeaway: "Show how to map a recurring task, define a useful result, and review an agent's work. Make the path from an idea to a working workflow concrete, with no coding required.",
  },
  {
    title: "Responsible AI for Leaders",
    audience: "For organizations putting AI into practice",
    takeaway: "Help leaders set expectations for AI use, protect sensitive information, and keep people accountable for decisions and results.",
  },
];

const appearances = [
  { show: "Let's Gig-A-Bit", title: "Episode 2: Turn Your Expertise into an AI-Powered Business", detail: "With Todd Rawlings and Mike Farabelli", href: "https://www.youtube.com/watch?v=adCU0b6VLTk", action: "Watch episode 2" },
  { show: "Let's Gig-A-Bit", title: "Episode 1: Smart AI Cost Management", detail: "With Todd Rawlings and Mike Farabelli", href: "https://www.youtube.com/watch?v=ZutirHMq9Ug", action: "Watch episode 1" },
  { show: "Growth@Scale", title: "Scaling Technical Leadership", detail: "Strategic technical hiring and growth", href: "https://www.mavan.com/strategic-technical-hiring-growth/", action: "Listen to the conversation" },
];

const trackSpeaking = (event: string, location: string) => trackEvent(event, {
  page: "/speak", location, speaking_revision: REVISION,
});

const AvailabilityLink = ({ location, children = "Check availability" }: { location: string; children?: ReactNode }) => (
  <Button asChild size="lg" className="speak-primary h-auto min-h-11 whitespace-normal px-5 py-3 text-base font-semibold">
    <a href="#speaking-request" onClick={() => trackSpeaking("Speaking Inquiry CTA Clicked", location)}>
      {children}<ArrowRight aria-hidden="true" className="h-4 w-4 shrink-0" />
    </a>
  </Button>
);


const Speak = () => {
  useEffect(() => { trackSpeaking("Speaking Page Viewed", "speaking_page"); }, []);

  return (
    <div className="speak-page min-h-screen bg-background text-foreground">
      <SEO
        title="AI Speaker & Workshop Facilitator Todd Larsen | Technical Leaders"
        description="Book a conversation about Todd Larsen speaking at your event. Practical AI keynotes, hands-on workshops, and podcast conversations on AI adoption, custom agents, and responsible leadership."
        keywords={['AI speaker', 'AI keynote speaker', 'AI workshop facilitator', 'Todd Larsen speaker', 'AI adoption speaker', 'responsible AI leadership']}
        image="https://www.technical-leaders.com/todd-speaking-stage.webp"
        imageAlt="Todd Larsen speaking on stage about practical AI adoption"
        imageWidth={2048}
        imageHeight={1152}
      />
      <a href="#speaking-content" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-20 focus:z-50 focus:bg-background focus:p-3 focus:text-foreground">Skip to speaking information</a>
      <Navigation hideCTA>
        <AvailabilityLink location="navigation">Inquire</AvailabilityLink>
      </Navigation>

      <main id="speaking-content" className="pt-20">
        <section className="relative overflow-hidden bg-background" aria-labelledby="speaking-heading">
          <div className="absolute inset-0 z-0">
            <img src="/todd-speaking-stage.webp" alt="Todd Larsen speaking on stage about practical AI adoption" width={2048} height={1152} fetchPriority="high" className="h-full w-full object-cover object-[18%_center] opacity-95 lg:object-center" />
            <div className="absolute inset-0 bg-[linear-gradient(to_top,hsl(var(--background))_0%,hsl(var(--background)/0.92)_55%,hsl(var(--background)/0.75)_100%)] lg:bg-[linear-gradient(to_right,hsl(var(--background))_0%,hsl(var(--background)/0.9)_45%,hsl(var(--background)/0.3)_85%)]" />
          </div>
          <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-4 py-12 sm:px-6 sm:py-16 lg:grid-cols-2 lg:px-8 lg:py-20">
            <div className="min-w-0">
              <p className="mb-4 text-sm font-semibold text-foreground">Todd Larsen · AI speaker & workshop facilitator</p>
              <h1 id="speaking-heading" className="max-w-xl text-4xl font-bold tracking-tight sm:text-5xl">Give your audience a practical way to put AI to work.</h1>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-foreground/85">Keynotes, hands-on workshops, and podcast conversations for leaders and teams moving from AI experimentation to everyday adoption.</p>
              <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3">
                <AvailabilityLink location="hero" />
                <a href="#speaking-topics" className="speak-text-link inline-flex min-h-11 items-center">Explore speaking topics</a>
              </div>
              <p className="mt-3 text-sm text-foreground/75">Share your event. We'll follow up within 48 hours.</p>
              <p className="mt-7 max-w-lg text-sm leading-relaxed text-foreground/80">Co-founder of Tech Leaders, an OpenAI SMB Channel Partner. Practical experience training teams and building custom AI agents.</p>
            </div>
            <figure className="min-w-0 overflow-hidden rounded-xl border border-border bg-background">
              <div className="aspect-video">
                <iframe src="https://www.youtube-nocookie.com/embed/5cCoBaP-H_0?rel=0" title="Todd Larsen | AI Speaking Demo" className="h-full w-full border-0" loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen />
              </div>
              <figcaption className="p-4">
                <span className="block font-semibold">Watch Todd speak: 4:39 speaking demo</span>
                <div className="mt-1 flex flex-wrap items-center gap-x-5">
                  <a href="https://www.youtube.com/watch?v=5cCoBaP-H_0" target="_blank" rel="noopener noreferrer" className="speak-text-link inline-flex min-h-11 items-center text-sm" onClick={() => trackSpeaking("Speaking Sample Clicked", "hero")}>Watch on YouTube</a>
                  <a href="/Todd Larsen - Media Kit 2026.pdf" download className="speak-text-link inline-flex min-h-11 items-center gap-2 text-sm" onClick={() => trackEvent("Media Kit Downloaded", { source: "speaking_page", version: "2026", speaking_revision: REVISION })}><Download aria-hidden="true" className="h-4 w-4" />Download media kit</a>
                </div>
              </figcaption>
            </figure>
          </div>
        </section>

        <section className="border-b border-border bg-secondary/30 px-4 py-10 sm:px-6 lg:px-8" aria-labelledby="participant-feedback-heading">
          <div className="mx-auto max-w-6xl">
            <h2 id="participant-feedback-heading" className="text-2xl font-bold sm:text-3xl">What previous attendees said</h2>
            <div className="mt-6 grid divide-y divide-border md:grid-cols-3 md:divide-x md:divide-y-0">
              {participantQuotes.map((quote) => (
                <blockquote key={quote} className="min-w-0 py-6 text-lg leading-relaxed text-foreground first:pt-0 last:pb-0 md:px-6 md:py-0 md:first:pl-0 md:last:pr-0">
                  <p>“{quote}”</p>
                </blockquote>
              ))}
            </div>
          </div>
        </section>

        <CompanyLogoMarquee className="dark:bg-gray-100" />

        <section id="speaking-topics" className="scroll-mt-24 px-4 py-16 sm:px-6 lg:px-8 lg:py-20" aria-labelledby="topics-heading">
          <div className="mx-auto grid max-w-6xl items-start gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
            <div>
              <p className="speak-eyebrow">Make it relevant to your room</p>
              <h2 id="topics-heading" className="mt-3 text-3xl font-bold sm:text-4xl">What should your audience do differently the next day?</h2>
              <p className="mt-5 text-lg leading-relaxed text-muted-foreground">Start with the work your audience knows. Choose a topic that helps them decide where AI fits, build something useful, or lead adoption across a team.</p>
              <img src="/todd-speaking-portrait.webp" alt="Todd Larsen presenting on stage with a microphone and presentation remote" width={1000} height={1250} loading="lazy" decoding="async" className="mt-8 h-64 w-full rounded-lg object-cover object-[center_25%] sm:h-80 lg:h-auto" />
            </div>
            <div>
              <div className="divide-y divide-border border-t border-border">
                {topics.map((topic) => (
                  <article key={topic.title} className="py-7">
                    <p className="text-sm font-semibold text-muted-foreground">{topic.audience}</p>
                    <h3 className="mt-2 text-2xl font-bold">{topic.title}</h3>
                    <p className="mt-3 text-base leading-relaxed text-muted-foreground">{topic.takeaway}</p>
                  </article>
                ))}
              </div>
              <div className="mt-3 rounded-lg bg-secondary p-6 text-secondary-foreground">
                <h3 className="text-xl font-semibold">Choose the format for your event</h3>
                <p className="mt-3 leading-relaxed">A keynote to give the room a shared framework. A hands-on workshop to apply it. A podcast conversation to explore the questions your listeners are asking.</p>
                <div className="mt-5"><AvailabilityLink location="topics">Discuss your event</AvailabilityLink></div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-border bg-secondary/30 px-4 py-14 sm:px-6 lg:px-8" aria-labelledby="appearances-heading">
          <div className="mx-auto max-w-6xl">
            <div className="mb-7 max-w-2xl"><p className="speak-eyebrow">Hear the conversation</p><h2 id="appearances-heading" className="mt-3 text-3xl font-bold">Past podcast appearances</h2><p className="mt-3 text-muted-foreground">Get a feel for Todd's approach before you reach out.</p></div>
            <div className="divide-y divide-border border-t border-border">
              {appearances.map((appearance) => (
                <article key={appearance.href} className="grid items-center gap-3 py-6 md:grid-cols-[1fr_2fr_auto] md:gap-8">
                  <p className="font-semibold">{appearance.show}</p>
                  <div><h3 className="text-xl font-semibold">{appearance.title}</h3><p className="mt-1 text-sm text-muted-foreground">{appearance.detail}</p></div>
                  <a href={appearance.href} target="_blank" rel="noopener noreferrer" className="speak-text-link inline-flex min-h-11 items-center gap-2 justify-self-start" onClick={() => trackEvent("Speaking Podcast Clicked", { page: "/speak", destination: appearance.href, speaking_revision: REVISION })}>{appearance.action}<ArrowRight aria-hidden="true" className="h-4 w-4" /></a>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="fees" className="scroll-mt-24 px-4 py-16 sm:px-6 lg:px-8" aria-labelledby="fees-heading">
          <div className="mx-auto max-w-6xl">
            <div className="mx-auto mb-8 max-w-3xl text-center">
              <p className="speak-eyebrow">Plan the engagement</p>
              <h2 id="fees-heading" className="mt-3 text-3xl font-bold sm:text-4xl">Speaking + workshop fees</h2>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">Clear starting points for your budget. Tell us about your audience, location, and format so we can confirm the right scope.</p>
            </div>
            <figure>
              <a
                href="/speaking-workshop-fee-map.svg"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open the speaking and workshop fee map at full size"
                className="block overflow-hidden rounded-2xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring"
              >
                <img
                  src="/speaking-workshop-fee-map.svg"
                  alt="World fee map showing previous speaking locations and starting fees: virtual $2,500; local in-person within a three-hour drive of Fayetteville, Arkansas $5,000; anywhere in the US $10,000; international $25,000."
                  width={1600}
                  height={860}
                  loading="lazy"
                  decoding="async"
                  className="h-auto w-full"
                />
              </a>
              <figcaption className="mt-3 text-center text-sm text-muted-foreground sm:hidden">Tap the fee map to view it full size.</figcaption>
            </figure>
            <p className="mt-6 text-sm leading-relaxed text-muted-foreground">Fees in USD. Final scope, audience, travel, recording, and production requirements are documented in the statement of work.</p>
          </div>
        </section>

        <section id="speaking-request" className="scroll-mt-24 border-y border-border bg-secondary/30 px-4 py-16 sm:px-6 lg:px-8 lg:py-20" aria-labelledby="request-heading">
          <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
            <div>
              <p className="speak-eyebrow">Let's talk about your event</p>
              <h2 id="request-heading" className="mt-3 text-3xl font-bold sm:text-4xl">Check Todd's availability.</h2>
              <p className="mt-5 text-lg leading-relaxed text-muted-foreground">Start with your contact details. Our team will follow up within 48 hours to discuss your event date, audience, and what you'd like them to take away.</p>
              <details className="mt-5">
                <summary className="speak-text-link min-h-11 cursor-pointer py-3 font-semibold">What happens next</summary>
              <ol className="mt-4 list-decimal space-y-3 pl-5 text-muted-foreground"><li>Share your contact details or email your event brief.</li><li>We'll discuss fit, availability, format, and scope.</li><li>Confirm the engagement in a statement of work.</li></ol>
              <p className="mt-6 text-sm text-muted-foreground">Sending an inquiry doesn't reserve a date or commit you to a booking.</p>
              </details>
            </div>
            <div className="min-w-0 rounded-xl border border-border bg-card p-5 text-card-foreground sm:p-8"><h3 className="mb-5 text-2xl font-bold">Your speaking inquiry</h3><SpeakingInquiry /></div>
          </div>
        </section>

        <section className="px-4 py-14 sm:px-6 lg:px-8" aria-labelledby="planning-heading">
          <div className="mx-auto max-w-3xl"><h2 id="planning-heading" className="mb-6 text-2xl font-bold">A few planning questions</h2>
            <div className="divide-y divide-border">
              <details className="py-3"><summary className="cursor-pointer py-3 text-lg font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">Is this right for a nontechnical audience?</summary><p className="pb-4 leading-relaxed text-muted-foreground">Yes. The focus is practical AI use, the work people already know, and how to judge a useful result. We can discuss your audience's experience when choosing the topic and format.</p></details>
              <details className="py-3"><summary className="cursor-pointer py-3 text-lg font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">Can I invite Todd onto a podcast?</summary><p className="pb-4 leading-relaxed text-muted-foreground">Yes. Use the inquiry form or email Amelia with your show link, audience, proposed topic, and recording dates. The episodes above give you a sense of the conversation.</p></details>
              <details className="py-3"><summary className="cursor-pointer py-3 text-lg font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">What should I include in an event brief?</summary><p className="pb-4 leading-relaxed text-muted-foreground">Your date or date range, location or virtual format, audience and approximate size, preferred topic, and budget. If you're still planning, start with what you know.</p></details>
            </div>
            <p className="mt-6 text-muted-foreground">Questions? <a href={CONTACT} className="speak-text-link break-words" onClick={() => trackSpeaking("Speaking Contact Email Clicked", "planning_questions")}>amelia@technical-leaders.com</a></p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Speak;
